// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * Auto-layout facade for Simlin diagrams.
 *
 * Implements D1 acceptance criteria 7-10 from the Systems Analyzer
 * Professional Services Agreement (Exhibit A):
 *
 *   7. Self-contained module with a pure-function interface: model in,
 *      positions out, no side effects, no React dependencies.
 *   8. Fully testable in isolation without the canvas, browser, or
 *      simulation engine.  Tests live in `tests/auto-layout.test.ts`.
 *   9. Public surface is documented with TypeScript types and JSDoc.
 *  10. No hard dependencies on a specific layout library in the public
 *      interface.  The backend is intentionally hidden: this module
 *      currently delegates to the WASM-compiled `simlin_engine::layout`
 *      pipeline through `@simlin/engine`, but callers cannot tell which
 *      algorithm runs underneath and the backend can be swapped without
 *      changing the facade signature.
 *
 * The facade is deliberately I/O-free: it accepts an SD-JSON project as
 * a string or plain object and returns a new project value with the
 * named model's view populated.  The input is never mutated.
 */

import { Project } from '@simlin/engine';

/**
 * SD-JSON project in either of two forms:
 *   - the raw on-disk string (UTF-8 encoded JSON), or
 *   - the equivalent already-parsed plain object.
 *
 * Both shapes round-trip through `JSON.stringify` / `JSON.parse` without
 * data loss.  The facade always returns the parsed-object form.
 */
export type SdProject = string | SdProjectObject;

/**
 * Loose typing for the native SD-JSON shape.  The contract here is
 * intentionally minimal -- we only inspect `models[*].name` and the
 * view positions.  Other fields are passed through unchanged.
 */
export interface SdProjectObject {
  models?: ReadonlyArray<{ name?: string; views?: unknown[] }>;
  [key: string]: unknown;
}

/**
 * Options accepted by {@link autoLayout}.
 */
export interface AutoLayoutOptions {
  /**
   * Which model in the project to lay out.  Defaults to the project's
   * main model when omitted.
   */
  modelName?: string;
}

/**
 * Compute auto-layout positions for an SD-JSON project.
 *
 * Pure function: the input project is parsed (if a string) and the
 * resulting object is treated as immutable.  The returned project is a
 * new value whose `models[i].views[0].elements` array carries
 * center-based pixel coordinates for every variable in the target
 * model.  When the input already has element positions for the target
 * model, those are preserved (incremental layout); otherwise a fresh
 * layout is generated from scratch.
 *
 * Example:
 * ```ts
 * import { autoLayout } from '@simlin/diagram/auto-layout';
 * const laidOut = await autoLayout(rawSdJsonString);
 * for (const elem of laidOut.models![0].views![0].elements) {
 *   console.log(elem.name, elem.x, elem.y);
 * }
 * ```
 *
 * @param project SD-JSON project as a string or plain object.
 * @param options See {@link AutoLayoutOptions}.
 * @returns A new project object whose target model's view contains
 *   laid-out elements.  The original input is never mutated.
 * @throws SimlinError when the project fails to parse, the target
 *   model is missing, or the layout backend reports an error.
 */
export async function autoLayout(
  project: SdProject,
  options: AutoLayoutOptions = {},
): Promise<SdProjectObject> {
  const jsonString = typeof project === 'string' ? project : JSON.stringify(project);

  // Project.openJson copies the JSON into WASM; the local string remains
  // intact, so the input is not aliased into the engine.  Format defaults
  // to native SD-JSON.
  const wasmProject = await Project.openJson(jsonString);
  try {
    // mainModel().name resolves to the model's display name via the
    // Rust FFI; it should always be set for a parseable project, but
    // fall back to 'main' if the engine returns null so the call still
    // works on minimal fixtures.
    const targetModel = options.modelName ?? (await wasmProject.mainModel()).name ?? 'main';
    await wasmProject.syncLayout(targetModel);
    const outString = await wasmProject.serializeJson();
    return JSON.parse(outString) as SdProjectObject;
  } finally {
    await wasmProject.dispose();
  }
}
